import moment from 'moment';
import React from 'react';
import Icon from '../../../components/icon/Icon';
import { regulationGoalColumns, timingSnapshotFromMatch } from '../../../helpers/matchTiming';
import { Props } from './index';

class MatchScoreboard extends React.PureComponent<Props> {
    public render = (): React.ReactElement => {
        const {
            match,
            competition,
            homeTeam,
            awayTeam,
            onBackToApp,
            onSaveMatchLog,
            onLivescoreClick,
            hasUnsavedMatchLogChanges,
            matchLogDirtyBaselineReady,
        } = this.props;

        const baselineReady = !!matchLogDirtyBaselineReady && !!match;
        const saveEnabled = baselineReady && !!onSaveMatchLog && !!hasUnsavedMatchLogChanges;
        const livescoreEnabled = baselineReady && !!onLivescoreClick && !hasUnsavedMatchLogChanges;

        return (
            <header className="c-game-log__header u-flex u-justify-content-between u-align-items-start">
                <div className="o-team-gl o-team-gl--white">
                    <div className="c-gl-toolbar u-flex u-align-items-center u-flex-wrap">
                        {onBackToApp && (
                            <button
                                type="button"
                                className="c-gl-toolbar__back u-flex u-align-items-center"
                                onClick={onBackToApp}
                            >
                                <Icon name="icon-swimmer" />
                                <span>Back to Match list</span>
                            </button>
                        )}
                        {onBackToApp && (onSaveMatchLog || (match && onLivescoreClick)) && (
                            <span className="c-gl-toolbar__sep" aria-hidden="true">
                                ·
                            </span>
                        )}
                        {onSaveMatchLog && (
                            <button
                                type="button"
                                className={`c-gl-toolbar__save u-flex u-align-items-center u-justify-content-center${
                                    saveEnabled ? ' c-gl-toolbar__save--dirty' : ''
                                }`}
                                onClick={onSaveMatchLog}
                                disabled={!saveEnabled}
                            >
                                <Icon name="icon-clipboard-check-regular" />
                                <span>Save match log</span>
                            </button>
                        )}
                        {onSaveMatchLog && match && onLivescoreClick && (
                            <span className="c-gl-toolbar__sep" aria-hidden="true">
                                ·
                            </span>
                        )}
                        {match && onLivescoreClick && (
                            <button
                                type="button"
                                className={`c-gl-toolbar__livescore u-flex u-align-items-center u-justify-content-center${
                                    livescoreEnabled ? ' c-gl-toolbar__livescore--ready' : ''
                                }`}
                                onClick={onLivescoreClick}
                                disabled={!livescoreEnabled}
                            >
                                <Icon name="icon-antena-tower" />
                                <span>Livescore</span>
                            </button>
                        )}
                    </div>
                    <div className="c-gl-team__team u-flex u-justify-content-start u-align-items-center">
                        <div className="c-gl-team__name">
                            <span>{homeTeam ? homeTeam.nameUniversal || homeTeam.name : ''}</span>
                        </div>
                    </div>
                </div>
                <div className="c-scoreboard">
                    <div className="c-scoreboard__top u-flex u-align-items-start">
                        <div className="c-scoreboard__date">
                            <span>
                                {match ? moment(match.startDate).format('DD.MM.YYYY. HH:mm') : ''}
                            </span>
                        </div>
                        <div className="c-scoreboard__name u-flex u-align-items-center">
                            <span>{match && match.round ? `Round: ${match.round}` : ''}</span>
                            {/* <Icon name="icon-edit-solid" /> */}
                        </div>
                    </div>

                    <div className="c-scoreboard__result u-flex u-align-items-center u-justify-content-center">
                        <span>
                            {match && match.homeTeamGoalsTotal !== null
                                ? match.homeTeamGoalsTotal
                                : ''}
                        </span>
                        <span>-</span>
                        <span>
                            {match && match.awayTeamGoalsTotal !== null
                                ? match.awayTeamGoalsTotal
                                : ''}
                        </span>
                    </div>

                    {match && (
                        <div className="c-scoreboard__quarter-results u-flex u-align-items-start u-justify-content-between">
                            {regulationGoalColumns(
                                match,
                                timingSnapshotFromMatch(match).periodCount
                            ).map((col) => (
                                <div key={col.label}>
                                    &nbsp;
                                    {`${col.home ?? 0} - ${col.away ?? 0}`}
                                    &nbsp;
                                </div>
                            ))}
                            <div>
                                &nbsp;
                                {match &&
                                match.homeTeamGoalsOT !== null &&
                                match.awayTeamGoalsOT !== null
                                    ? `${match.homeTeamGoalsOT} - ${match.awayTeamGoalsOT}`
                                    : ''}
                                &nbsp;
                            </div>
                        </div>
                    )}
                </div>
                <div className="o-team-gl o-team-gl--blue">
                    <p className="c-gl-team__note c-gl-team__note--meta u-flex u-flex-wrap u-justify-content-end u-align-items-center">
                        <span className="c-gl-team__note-chunk u-flex u-align-items-center">
                            <Icon name="icon-map-marker-alt" />
                            <span>
                                {match
                                    ? `Venue: ${match.venue || ''}${
                                          match.venue && match.city ? ', ' : ''
                                      }${match.city || ''}`
                                    : ''}
                            </span>
                        </span>
                        <span className="c-gl-team__note-chunk u-flex u-align-items-center">
                            <Icon name="icon-stopwatch" />
                            <span>
                                {match && match.status
                                    ? `Status: ${match.status.replace('_', ' ')}`
                                    : ''}
                            </span>
                        </span>
                        {competition && (
                            <span
                                className="c-gl-team__note-chunk c-gl-team__note-chunk--competition u-flex u-align-items-center"
                                title={competition.nameUniversal || competition.name || undefined}
                            >
                                <Icon name="icon-trophy" />
                                <span className="c-gl-team__note-competition-text">
                                    {competition.nameUniversal || competition.name}
                                </span>
                            </span>
                        )}
                    </p>
                    <div className="c-gl-team__team u-flex u-justify-content-end u-align-items-center">
                        <div className="c-gl-team__name">
                            <span>{awayTeam ? awayTeam.nameUniversal || awayTeam.name : ''}</span>
                        </div>
                    </div>
                </div>
            </header>
        );
    };
}

export default MatchScoreboard;
